import { SCOPE_BOX_LABEL } from "@/api/roles-permissions/roleScope";
import type { UserScopeCompany } from "@/api/access/userScope.types";
import type { CategoryBox, ScopeWrite, WritableBox } from "@/api/access/userScope";

export type RuleKind = "inclusion" | "exclusion";

/**
 * One Include By / Exclude By row, as the sketches' `incGroup` draws it: a box to limit by, and
 * the values to limit it to. A box the role *requires* is seeded as a row that cannot be removed
 * and must be filled; everything else the person's roles put in play can be added on demand.
 */
export type ScopeRule = {
  /** Stable across re-orders so React and the pickers keep their place. */
  key: string;
  kind: RuleKind;
  /** Null while a freshly added row has not been pointed at a box yet. */
  box: WritableBox | null;
  ids: string[];
  /** A required box: its row is always present, always an inclusion, and cannot be deleted. */
  required: boolean;
};

export const boxLabel = (box: WritableBox): string => SCOPE_BOX_LABEL[box];

/** The boxes a role of this person's puts in play here. A write to any other is refused. */
export const boxesInPlay = (company: UserScopeCompany): WritableBox[] =>
  company.boxes
    .filter((entry) => entry.box !== "entities" && entry.kind !== "not_in_play")
    .map((entry) => entry.box as WritableBox);

export const requiredBoxes = (company: UserScopeCompany): WritableBox[] =>
  company.boxes
    .filter((entry) => entry.box !== "entities" && entry.required)
    .map((entry) => entry.box as WritableBox);

const unique = (ids: string[]): string[] => [...new Set(ids)];

let seq = 0;
const nextKey = () => `rule-${(seq += 1)}`;

export const newRule = (kind: RuleKind): ScopeRule => ({
  key: nextKey(),
  kind,
  box: null,
  ids: [],
  required: false,
});

/**
 * The rows a person's stored scope already amounts to, plus a seeded row for every required box
 * that has nothing in it yet.
 *
 * Clients are read from the assignment as well as the rule: the engine unions them
 * (`scope.clientScopeOf`), so a row built from the rule alone would hide the assigned clients and
 * the next save would drop them.
 */
export const rulesFromScope = (company: UserScopeCompany): ScopeRule[] => {
  const required = new Set(requiredBoxes(company));
  const inPlay = new Set(boxesInPlay(company));
  const rules: ScopeRule[] = [];

  const { assignment, rule: clientRule } = company.clients;
  const clientInclusion = unique([
    ...(assignment?.mode === "chosen" ? assignment.clientIds : []),
    ...(clientRule?.type === "inclusion" ? clientRule.ids : []),
  ]);
  const clientExclusion = clientRule?.type === "exclusion" ? clientRule.ids : [];

  // Both, not one or the other: a chosen list *and* an exclusion is a state the engine allows
  // (`clientScopeOf`), and showing only the first would delete the other on the next save.
  if (clientInclusion.length > 0) {
    rules.push({
      key: nextKey(),
      kind: "inclusion",
      box: "clients",
      ids: clientInclusion,
      required: required.has("clients"),
    });
  }
  if (clientExclusion.length > 0) {
    rules.push({
      key: nextKey(),
      kind: "exclusion",
      box: "clients",
      ids: clientExclusion,
      required: false,
    });
  }

  for (const [box, rule] of Object.entries(company.rules)) {
    // A rule on a box no role puts in play is kept by the backend but cannot be edited here —
    // the type dropdown would not offer it and deleting the row would do nothing.
    if (!rule || rule.ids.length === 0 || !inPlay.has(box as WritableBox)) continue;
    rules.push({
      key: nextKey(),
      kind: rule.type,
      box: box as CategoryBox,
      ids: rule.ids,
      required: required.has(box as WritableBox),
    });
  }

  /*
   * A required box with nothing in it is the whole reason this modal is open, so it gets a row
   * waiting to be filled rather than an "Add Inclusion" the user has to discover.
   *
   * What counts as answered differs by box, exactly as the engine counts it: a category box is
   * filled by a rule of **either** type (`companyView`'s `isEmpty` is `!rules[box]`), while
   * clients need an inclusion or an `all` assignment — an exclusion alone leaves the person with
   * nothing, so treating it as the answer would show "Set" over someone who reaches nothing.
   */
  for (const box of requiredBoxes(company)) {
    const answered =
      box === "clients"
        ? assignment?.mode === "all" ||
          rules.some((rule) => rule.box === box && rule.kind === "inclusion")
        : rules.some((rule) => rule.box === box);
    if (answered) continue;
    rules.push({ key: nextKey(), kind: "inclusion", box, ids: [], required: true });
  }
  return rules;
};

/**
 * Which boxes a row may point at: in play here, and not already taken.
 *
 * A **category** box holds one rule of one type — `scopeService.setRule` answers 409 while a rule
 * of the other type exists — so one row per box, whichever kind. **Clients** are the exception:
 * the chosen list is an assignment and the exclusion is a rule, so an inclusion row and an
 * exclusion row can live side by side (the engine reads them together in `clientScopeOf`).
 */
const boxTaken = (rules: ScopeRule[], box: WritableBox, kind: RuleKind, exceptKey?: string) =>
  rules.some(
    (rule) =>
      rule.key !== exceptKey && rule.box === box && (box === "clients" ? rule.kind === kind : true),
  );

export const boxOptionsFor = (
  company: UserScopeCompany,
  rules: ScopeRule[],
  row: ScopeRule,
): WritableBox[] => boxesInPlay(company).filter((box) => !boxTaken(rules, box, row.kind, row.key));

/** True when every box in play already has a row, so adding another of this kind leads nowhere. */
export const noBoxLeftFor = (
  company: UserScopeCompany,
  rules: ScopeRule[],
  kind: RuleKind,
): boolean => boxesInPlay(company).every((box) => boxTaken(rules, box, kind));

/** A required row the user has not filled in. Saving is allowed; it just changes nothing. */
export const unfilledRequired = (rules: ScopeRule[]): ScopeRule[] =>
  rules.filter((rule) => rule.required && rule.ids.length === 0);

/**
 * A row the user added and left half-made: a box with no values, or no box at all.
 *
 * It must block the save rather than be dropped — `planRuleWrites` writes nothing for it, so
 * saving over it would silently throw away a limit the user believes they just added. The
 * required row is deliberately excluded: leaving it empty is how you move past someone you are
 * not ready to answer for, and blocking on it would strand the rest of the queue.
 */
export const incompleteRules = (rules: ScopeRule[]): ScopeRule[] =>
  rules.filter((rule) => !rule.required && (rule.box === null || rule.ids.length === 0));

const idsFor = (rules: ScopeRule[], box: WritableBox, kind: RuleKind): string[] | null => {
  const rule = rules.find((entry) => entry.box === box && entry.kind === kind);
  return rule && rule.ids.length > 0 ? rule.ids : null;
};

const same = (a: string[] | null, b: string[] | null) =>
  a === null ? b === null : b !== null && a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * The requests these rows amount to, against what is stored now.
 *
 * Two passes, not one. Every delete goes before every write, so a box changing from one rule
 * type to the other is cleared before the new one lands — `scopeService.setRule` answers 409
 * otherwise — and the client assignment, the write that can widen access the most, goes last of
 * all, so a failure part-way leaves the person with less than intended rather than more.
 */
export const planRuleWrites = (company: UserScopeCompany, rules: ScopeRule[]): ScopeWrite[] => {
  const deletes: ScopeWrite[] = [];
  const writes: ScopeWrite[] = [];
  let setClients: ScopeWrite | null = null;

  const { assignment, rule: storedRule } = company.clients;
  const storedInclude = (() => {
    const ids = unique([
      ...(assignment?.mode === "chosen" ? assignment.clientIds : []),
      ...(storedRule?.type === "inclusion" ? storedRule.ids : []),
    ]);
    return ids.length > 0 ? ids : null;
  })();
  const storedExclude =
    storedRule?.type === "exclusion" && storedRule.ids.length > 0 ? storedRule.ids : null;

  const wantInclude = idsFor(rules, "clients", "inclusion");
  const wantExclude = idsFor(rules, "clients", "exclusion");
  const includeChanged = !same(wantInclude, storedInclude);
  const excludeChanged = !same(wantExclude, storedExclude);

  if (includeChanged || excludeChanged) {
    /*
     * The one clients rule slot can only hold the exclusion, because an inclusion is written as
     * the assignment. So a stored inclusion rule has to go whenever its ids move into the
     * assignment, and also whenever an exclusion needs the slot.
     */
    const ruleMustGo =
      storedRule !== null &&
      (storedRule.type === "inclusion"
        ? includeChanged || wantExclude !== null
        : wantExclude === null);
    if (ruleMustGo) deletes.push({ op: "removeRule", box: "clients" });

    if (wantExclude !== null) {
      writes.push({ op: "setRule", box: "clients", type: "exclusion", ids: wantExclude });
    }

    // Also rewrite the assignment when an inclusion rule is being taken away for the exclusion,
    // or its ids would go with it.
    const rewriteAssignment =
      includeChanged || (storedRule?.type === "inclusion" && wantExclude !== null);
    if (rewriteAssignment) {
      if (wantInclude !== null) {
        setClients = { op: "setClients", mode: "chosen", clientIds: wantInclude };
      } else if (assignment?.mode === "chosen") {
        // Never on `mode: "all"`: that is a grant this screen has no control for, and clearing
        // it would quietly take away every client the person had.
        deletes.push({ op: "clearClients" });
      }
    }
  }

  for (const box of boxesInPlay(company)) {
    if (box === "clients") continue;
    const stored = company.rules[box as CategoryBox];
    const row = rules.find((entry) => entry.box === box && entry.ids.length > 0);
    const storedIds = stored && stored.ids.length > 0 ? stored.ids : null;
    const wantIds = row ? row.ids : null;
    const storedKind = stored?.type ?? null;
    const wantKind = row?.kind ?? null;

    if (same(storedIds, wantIds) && storedKind === wantKind) continue;
    if (wantIds === null) {
      if (storedIds !== null) deletes.push({ op: "removeRule", box: box as CategoryBox });
      continue;
    }
    if (storedKind !== null && storedKind !== wantKind) {
      deletes.push({ op: "removeRule", box: box as CategoryBox });
    }
    writes.push({ op: "setRule", box: box as CategoryBox, type: wantKind!, ids: wantIds });
  }

  return [...deletes, ...writes, ...(setClients ? [setClients] : [])];
};
